import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader2, GitBranch, ChevronRight, Target, Award, Clock } from "lucide-react";
import { fetchTenantLibrary } from "../lib/tenantApi";
import TenantShell from "../components/TenantShell";

const BAND_COLORS = {
  A: "#94a3b8",
  B: "#64748b",
  C: "#0ea5e9",
  D: "#0284c7",
  E: "#0c4a6e",
};

const SEGMENT_LABELS = {
  RETAIL: "Mağaza / Perakende",
  PROD: "Üretim",
  HQ: "Merkez Ofis",
};

function StepNode({ step, kpiSets, compIndex, isLast, onClick, active }) {
  const bandColor = BAND_COLORS[step.band] || "#0ea5e9";
  return (
    <div className="flex items-stretch gap-0 min-w-0">
      <button
        onClick={onClick}
        data-testid={`ladder-step-${step.band}`}
        className={`flex-shrink-0 w-56 text-left p-4 rounded-xl border-2 transition-all hover:shadow-md bg-white ${
          active ? "shadow-md" : ""
        }`}
        style={{ borderColor: active ? bandColor : "#e2e8f0" }}
      >
        <div className="flex items-center justify-between mb-2">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ background: bandColor }}
          >
            {step.band}
          </div>
          {step.duration ? (
            <div className="flex items-center gap-1 text-[0.65rem] text-slate-500">
              <Clock className="w-3 h-3" />
              {step.duration}
            </div>
          ) : null}
        </div>
        <div className="font-display font-semibold text-sm text-slate-900 leading-tight mb-1">
          {step.role_label}
        </div>
        <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider font-semibold">
          {(step.required_competencies?.length || 0)} yetkinlik · {(step.target_kpis?.length || 0)} KPI
        </div>
      </button>
      {!isLast && (
        <div className="flex items-center px-1 text-slate-300">
          <ChevronRight className="w-5 h-5" />
        </div>
      )}
    </div>
  );
}

function StepDetail({ step, compIndex, kpiIndex }) {
  if (!step) return null;
  const bandColor = BAND_COLORS[step.band] || "#0ea5e9";
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-6 mt-6"
      data-testid="ladder-step-detail"
      style={{ borderTop: `3px solid ${bandColor}` }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold"
          style={{ background: bandColor }}
        >
          {step.band}
        </div>
        <div>
          <div className="font-display text-xl font-bold text-slate-900">{step.role_label}</div>
          <div className="text-xs text-slate-500">
            Beklenen süre: {step.duration || "—"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-3">
            <Award className="w-3.5 h-3.5" /> Gereken Yetkinlikler
          </div>
          <div className="space-y-1.5">
            {step.required_competencies?.length ? (
              step.required_competencies.map((rc) => {
                const info = compIndex[rc.comp_id];
                return (
                  <div
                    key={rc.comp_id}
                    className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-200"
                    data-testid={`req-comp-${rc.comp_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-mono text-[0.65rem] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 flex-shrink-0">
                        {rc.comp_id}
                      </span>
                      <span className="text-sm text-slate-800 truncate">
                        {info?.name || rc.comp_id}
                      </span>
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0 ml-2">
                      {[1, 2, 3, 4, 5].map((l) => (
                        <div
                          key={l}
                          className={`w-3 h-3 rounded-sm ${
                            l <= rc.target_level ? "" : "bg-slate-200"
                          }`}
                          style={l <= rc.target_level ? { background: bandColor } : {}}
                          title={`Seviye ${l}`}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-slate-400 italic">Bu kademe için yetkinlik tanımlı değil.</div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-3">
            <Target className="w-3.5 h-3.5" /> Hedef KPI'lar
          </div>
          <div className="space-y-1.5">
            {step.target_kpis?.length ? (
              step.target_kpis.map((kid) => {
                const k = kpiIndex[kid];
                return (
                  <div
                    key={kid}
                    className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-xs"
                    data-testid={`req-kpi-${kid}`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-semibold text-slate-900">{k?.name || kid}</span>
                      <span className="font-mono text-[0.65rem] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600">
                        {kid}
                      </span>
                    </div>
                    {k?.desc ? <div className="text-slate-500 leading-snug">{k.desc}</div> : null}
                    {k?.thresholds ? (
                      <div className="mt-1.5 flex gap-2 text-[0.6rem]">
                        <span className="text-emerald-600 font-medium">≥{k.thresholds.green}{k.unit}</span>
                        <span className="text-amber-600 font-medium">{k.thresholds.amber}{k.unit}+</span>
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-slate-400 italic">Bu kademe için KPI tanımlı değil.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CareerLadders() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLadder, setActiveLadder] = useState(null);
  const [activeStep, setActiveStep] = useState(null);

  useEffect(() => {
    fetchTenantLibrary(slug)
      .then((d) => {
        setData(d);
        if (d.career_ladders?.[0]) {
          setActiveLadder(d.career_ladders[0].id);
          setActiveStep(d.career_ladders[0].steps?.[0]?.band || null);
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Build comp index
  const compIndex = useMemo(() => {
    const idx = {};
    data?.skill_clusters?.forEach((c) =>
      c.skills?.forEach((s) => {
        idx[s.id] = s;
      }),
    );
    return idx;
  }, [data]);

  const kpiIndex = useMemo(() => {
    const idx = {};
    data?.kpi_sets?.forEach((k) => {
      idx[k.id] = k;
    });
    return idx;
  }, [data]);

  const ladder = useMemo(
    () => data?.career_ladders?.find((l) => l.id === activeLadder),
    [data, activeLadder],
  );

  const step = useMemo(
    () => ladder?.steps?.find((s) => s.band === activeStep),
    [ladder, activeStep],
  );

  // Group ladders by segment
  const ladderGroups = useMemo(() => {
    const g = {};
    data?.career_ladders?.forEach((l) => {
      const seg = l.segment || "OTHER";
      g[seg] = g[seg] || [];
      g[seg].push(l);
    });
    return g;
  }, [data]);

  if (loading) {
    return (
      <TenantShell active="career-paths">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </TenantShell>
    );
  }

  if (!data) {
    return (
      <TenantShell active="career-paths">
        <div className="text-center py-20">
          <p className="text-sm text-slate-500">Kariyer yolu kütüphanesi bulunamadı.</p>
        </div>
      </TenantShell>
    );
  }

  return (
    <TenantShell active="career-paths">
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
          <GitBranch className="w-3.5 h-3.5" />
          Sektörel Kariyer Yolları
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2">
          Kariyer Yolları
        </h1>
        <p className="text-slate-600 max-w-2xl">
          {data.career_ladders?.length || 0} kariyer yolu · Band A (giriş) → Band E (lider). Her kademe için
          gereken yetkinlikler ve hedef KPI'lar tanımlanmıştır.
        </p>
      </div>

      {/* Ladder selector grouped by segment */}
      <div className="space-y-4 mb-8">
        {Object.entries(ladderGroups).map(([seg, ladders]) => (
          <div key={seg}>
            <div className="text-[0.65rem] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
              {SEGMENT_LABELS[seg] || seg}
            </div>
            <div className="flex flex-wrap gap-2">
              {ladders.map((l) => (
                <button
                  key={l.id}
                  onClick={() => {
                    setActiveLadder(l.id);
                    setActiveStep(l.steps?.[0]?.band || null);
                  }}
                  data-testid={`ladder-tab-${l.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    activeLadder === l.id
                      ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {l.name}
                  <span className="ml-2 opacity-60 text-xs">{l.steps?.length || 0} kademe</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Interactive ladder visualization */}
      {ladder && (
        <>
          <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">{ladder.name}</h3>
                <p className="text-xs text-slate-500">Bir kademeye tıklayarak gereken yetkinlikleri görün</p>
              </div>
              <span className="text-[0.7rem] text-slate-400 uppercase tracking-wider font-semibold">
                {SEGMENT_LABELS[ladder.segment] || ladder.segment}
              </span>
            </div>
            <div className="overflow-x-auto pb-3 -mx-2 px-2">
              <div className="flex items-stretch min-w-min">
                {ladder.steps?.map((s, i) => (
                  <StepNode
                    key={s.band}
                    step={s}
                    isLast={i === ladder.steps.length - 1}
                    active={activeStep === s.band}
                    onClick={() => setActiveStep(s.band)}
                    compIndex={compIndex}
                    kpiSets={data.kpi_sets}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Selected step detail */}
          <StepDetail step={step} compIndex={compIndex} kpiIndex={kpiIndex} />
        </>
      )}
    </TenantShell>
  );
}
